import { Link } from "react-router-dom";
import {
    Bell,
    PhoneCall,
    Snowflake,
    Thermometer,
    Activity,
    Factory,
    Package,
    ShieldAlert,
    Building2,
    Puzzle,
    Users,
    Radio,
    MapPin,
    RefreshCw,
    Workflow,
    FileText,
    ArrowRight,
    ArrowLeft,
} from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { Footer } from "../components/sections/Footer";
import { DowntimeSection } from "../components/sections/DowntimeSection";
import useReveal from "../lib/useReveal";

const integrations = [
    { icon: Bell, label: "Emergency Buttons" },
    { icon: PhoneCall, label: "Emergency Call Boxes" },
    { icon: Snowflake, label: "Cryogenic / LN₂ Tanks" },
    { icon: Thermometer, label: "Temperature Monitoring" },
    { icon: Activity, label: "Equipment Alarms" },
    { icon: Factory, label: "Manufacturing Equipment" },
    { icon: Package, label: "Warehouse Safety" },
    { icon: ShieldAlert, label: "Security Systems" },
    { icon: Building2, label: "Facility Monitoring" },
    { icon: Puzzle, label: "Custom Integrations" },
];

const workflow = [
    { icon: Users, title: "Notify designated response personnel" },
    { icon: Radio, title: "Deliver immediate alerts" },
    { icon: MapPin, title: "Identify the exact location of the emergency" },
    { icon: RefreshCw, title: "Escalate notifications if no response is received" },
    { icon: Workflow, title: "Improve emergency communication workflows" },
    { icon: FileText, title: "Create activation records for reporting and review" },
];

const featured = [
    {
        icon: Factory,
        title: "Manufacturing",
        desc: "Improve emergency communication by connecting equipment, emergency buttons, and facility safety systems into one coordinated response platform.",
    },
    {
        icon: Snowflake,
        title: "Cryogenic & LN₂ Tank Response",
        desc: "Connect alert buttons on LN₂ and cryogenic tanks to Kairos so the right personnel are notified the instant someone triggers a manual alert.",
    },
    {
        icon: Building2,
        title: "Facility Emergency Response",
        desc: "Modernize existing emergency buttons and call boxes with intelligent outreach, location awareness, and faster internal response.",
    },
];

export default function EmergencyOutreachPage() {
    useReveal();
    return (
        <main data-testid="emergency-outreach-page" className="min-h-screen bg-white">
            <Navbar />

            {/* Hero */}
            <section className="relative bg-[#0A1428] text-white pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-32 -right-24 w-[520px] h-[520px] opacity-[0.08] pointer-events-none float-slow">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>
                <div className="absolute -bottom-40 -left-20 w-[520px] h-[520px] opacity-[0.05] pointer-events-none float-slower">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 100,40 180,160" fill="#3B82F6" />
                    </svg>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <Link
                        to="/"
                        data-testid="outreach-back"
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-300 hover:text-white transition-colors mb-10"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                        Back to Kairos
                    </Link>

                    <div className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Kairos · Alert &amp; Automation
                    </div>
                    <h1 className="mt-6 font-display font-extrabold text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-tighter text-balance max-w-4xl">
                        Connected alerts &amp; automation
                        <br />
                        for modern facilities.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed font-medium">
                        Kairos Alert &amp; Automation helps organizations
                        modernize emergency and facility communication by
                        connecting existing safety systems with intelligent
                        notifications, sensor-driven alerts, and coordinated
                        response workflows.
                    </p>
                    <a
                        href="#outreach-contact"
                        data-testid="outreach-cta-top"
                        className="btn-primary mt-10 inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm"
                    >
                        Discuss your facility
                        <ArrowRight size={18} strokeWidth={2} />
                    </a>
                </div>
            </section>

            {/* Downtime Prevention — platform-level story */}
            <DowntimeSection />

            {/* Product Showcases */}
            <ProductShowcase
                id="ln2-scale"
                testid="ln2-showcase"
                kicker="Product · LN₂ Scale + Workflow Automation"
                title={<>One press.<br />The right people notified.</>}
                description="The Kairos LN₂ Scale is a rugged, wall-mounted device staff press when a liquid nitrogen tank is running low or empty. A single press instantly routes an alert to the personnel responsible for the refill and triggers the connected workflow — no ambiguity, no missed messages."
                bullets={[
                    "One-touch activation — designed for gloved hands and busy environments",
                    "LED status indicator confirms a refill is in progress",
                    "Powered-by-Kairos hardware — integrates with your existing team workflow",
                    "Weather-hardened enclosure engineered for facility, lab, and industrial use",
                ]}
                media={{
                    type: "image",
                    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_narcan-dispenser-pro/artifacts/3myku7rb_IMG_1213.jpg",
                    alt: "Kairos LN₂ Scale Alert Button — press-when-empty device",
                }}
                badge="The Device"
                bg="bg-slate-50"
            />

            <ProductShowcase
                id="equipment-alerts"
                testid="equipment-alerts-showcase"
                kicker="Product · Equipment Alerts"
                title={<>Turn a press into<br />a work order.</>}
                description="The Kairos Equipment Alert dispatches a work order the moment a technician or floor operator flags an issue. One press captures the equipment, the timestamp, and the person on the hook to respond — no radios, no paperwork, no waiting for the next shift meeting."
                bullets={[
                    "Instant work-order creation — routed to the right technician or department",
                    "LED indicator shows when a work order is already in progress",
                    "Rugged enclosure — built for shop floors, warehouses, and manufacturing lines",
                    "Audit-grade event log for compliance and continuous-improvement reviews",
                ]}
                media={{
                    type: "image",
                    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_narcan-dispenser-pro/artifacts/xtr57xxf_03e71a79-6d3a-463e-8591-b443fe462013.png",
                    alt: "Kairos Equipment Alert — press to create equipment work order device",
                }}
                badge="The Device"
                bg="bg-white"
                reverse
            />

            <ProductShowcase
                id="automated-weight-alerts"
                testid="automated-weight-showcase"
                kicker="Product · Automated Weight Alerts"
                title={<>The scale watches.<br />You get notified.</>}
                description="Automated Weight Alerts pair an industrial-grade scale with the Kairos platform, so refills, deliveries, and consumption thresholds are tracked continuously. When a weight crosses a threshold, the right people know — automatically, and long before anyone runs out."
                bullets={[
                    "Continuous weight monitoring — no manual checks required",
                    "Custom thresholds trigger the right alert to the right person",
                    "Ideal for tank, cylinder, chemical, or consumable inventory",
                    "Every event logged for audit, forecasting, and restocking",
                ]}
                media={{
                    type: "video",
                    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_narcan-dispenser-pro/artifacts/9167zdcx_Industrial_scale_transforming_co%E2%80%A6_202608241818.mp4",
                }}
                badge="In Motion"
                bg="bg-slate-50"
            />

            {/* Integrations · Custom Alert Systems */}
            <section
                id="custom-alert-systems"
                className="relative bg-white py-24 md:py-32 border-b border-slate-200 scroll-mt-24"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="reveal max-w-3xl">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Custom Alert Systems · Integrations
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                            Works with the safety
                            <br />
                            infrastructure you already have.
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                        {integrations.map((i, idx) => (
                            <div
                                key={i.label}
                                data-testid={`outreach-integration-${idx}`}
                                className="reveal group bg-white p-6 md:p-8 flex flex-col items-start hover:bg-slate-50 transition-colors"
                            >
                                <div className="h-11 w-11 grid place-items-center border border-[#0A1428] group-hover:border-[#F0493E] transition-colors">
                                    <i.icon size={18} strokeWidth={1.5} className="text-[#F0493E]" />
                                </div>
                                <div className="mt-5 font-display font-bold text-base md:text-lg text-[#0A1428] tracking-tight leading-snug">
                                    {i.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Workflow */}
            <section className="relative bg-slate-50 py-24 md:py-32 border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="reveal max-w-3xl">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            When a System Is Activated
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                            What Kairos does
                            <br />
                            the moment it matters.
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {workflow.map((w, i) => (
                            <div
                                key={w.title}
                                data-testid={`outreach-workflow-${i}`}
                                className="reveal bg-white border border-slate-200 p-6 md:p-8 flex items-start gap-4 hover:border-[#F0493E] transition-colors"
                            >
                                <div className="shrink-0 h-11 w-11 grid place-items-center border border-[#0A1428]">
                                    <w.icon size={18} strokeWidth={1.5} className="text-[#F0493E]" />
                                </div>
                                <p className="font-display font-bold text-base md:text-lg text-[#0A1428] leading-snug tracking-tight">
                                    {w.title}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured applications */}
            <section className="relative bg-white py-24 md:py-32 border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="reveal max-w-3xl">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Featured Applications
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                            Where Emergency Outreach
                            <br />
                            is making the biggest difference.
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {featured.map((f, i) => (
                            <div
                                key={f.title}
                                data-testid={`outreach-featured-${i}`}
                                className="reveal bg-slate-50 border border-slate-200 p-8 md:p-10 flex flex-col hover:border-[#0A1428] transition-colors"
                            >
                                <div className="h-12 w-12 grid place-items-center border border-[#0A1428]">
                                    <f.icon size={22} strokeWidth={1.5} className="text-[#F0493E]" />
                                </div>
                                <h3 className="mt-8 font-display font-bold text-2xl md:text-3xl text-[#0A1428] tracking-tight leading-[1.05]">
                                    {f.title}
                                </h3>
                                <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed font-medium flex-1">
                                    {f.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA strip */}
            <section id="outreach-contact" className="relative bg-[#0A1428] text-white py-20 md:py-28 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-[420px] h-[420px] opacity-[0.08] pointer-events-none">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <div>
                        <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                            Talk to Kairos
                        </div>
                        <h3 className="mt-4 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-balance max-w-2xl">
                            Bring intelligent emergency response
                            <br />
                            to your facility.
                        </h3>
                    </div>
                    <Link
                        to="/contact"
                        data-testid="outreach-cta-bottom"
                        className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm self-start md:self-end"
                    >
                        Start a conversation
                        <ArrowRight size={18} strokeWidth={2} />
                    </Link>
                </div>
            </section>

            <Footer />
        </main>
    );
}

const ProductShowcase = ({
    id,
    testid,
    kicker,
    title,
    description,
    bullets,
    media,
    badge,
    bg = "bg-slate-50",
    reverse = false,
}) => (
    <section
        id={id}
        data-testid={testid}
        className={`relative ${bg} py-24 md:py-32 border-b border-slate-200 overflow-hidden scroll-mt-24`}
    >
        <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div
                className={`lg:col-span-6 reveal ${
                    reverse ? "order-2 lg:order-2" : "order-2 lg:order-1"
                }`}
            >
                <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                    {kicker}
                </span>
                <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                    {title}
                </h2>
                <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed font-medium max-w-xl">
                    {description}
                </p>
                <ul className="mt-8 space-y-3">
                    {bullets.map((b) => (
                        <li
                            key={b}
                            className="flex items-start gap-3 text-sm md:text-[15px] text-slate-700 leading-relaxed font-medium"
                        >
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#F0493E] shrink-0" />
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div
                className={`lg:col-span-6 reveal ${
                    reverse ? "order-1 lg:order-1" : "order-1 lg:order-2"
                }`}
            >
                <div className="relative bg-[#0A1428] p-6 md:p-8 border border-slate-200 shadow-[0_40px_80px_-30px_rgba(10,20,40,0.35)]">
                    {media.type === "video" ? (
                        <video
                            src={media.src}
                            data-testid={`${testid}-video`}
                            className="w-full h-auto object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                        />
                    ) : (
                        <img
                            src={media.src}
                            alt={media.alt}
                            data-testid={`${testid}-image`}
                            className="w-full h-auto object-cover"
                            loading="lazy"
                        />
                    )}
                    <div className="absolute -top-3 -left-3 bg-[#F0493E] text-white text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1.5">
                        {badge}
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-slate-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#F0493E]" />
                    Powered by Kairos · Built in the United States
                </div>
            </div>
        </div>
    </section>
);

const ShowcaseItem = null; // deprecated — bullets now inline in ProductShowcase
